// iMoney Malaysia Referral System - Mutable In-Memory Demo Database
// Wraps mockData.js with CRUD operations. Changes persist within browser session only.

import {
  users as rawUsers,
  products as rawProducts,
  cases as rawCases,
  partners as rawPartners,
  commissionSchemes as rawCommissionSchemes,
  rewardConfig as rawRewardConfig,
  notifications as rawNotifications,
  campaigns as rawCampaigns,
  marketingMaterials as rawMarketingMaterials,
} from './mockData';
import {
  initialProductConfigs as rawProductConfigs,
  initialAuditLogs as rawAuditLogs,
  computeContractStatus,
} from './productConfigData';
import { calculateCommissionForCase, mapProductToCategory } from '../utils/commissionCalculator';

// ── Deep clone helper ──────────────────────────────────────────────
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// ── Role migration map ─────────────────────────────────────────────
const ROLE_MAP = {
  applicant: 'free_user',
  agent: 'individual_agent',
  corporate_partner: 'partner',
  admin: 'admin',
};

// Pre-verified user IDs (seeded verified for demo purposes)
const PRE_VERIFIED_USER_IDS = new Set(['USR001', 'USR005', 'USR007']);

// ── Initialise mutable collections ─────────────────────────────────
const _users = deepClone(rawUsers).map((u) => {
  const isPreVerified = PRE_VERIFIED_USER_IDS.has(u.id);
  return {
  ...u,
  role: ROLE_MAP[u.role] || u.role,
  password: u.email === 'admin@imoney.my' ? 'admin123' : 'demo123',
  // Preserve any seeded referredBy from mockData (used for downline demos)
  referredBy: u.referredBy || null,
  downline: [],
  // eKYC fields — default not_started, pre-verified demo users get verified
  kycStatus: isPreVerified ? 'verified' : (u.kycStatus || 'not_started'),
  idVerified: isPreVerified,
  faceVerified: isPreVerified,
  ctosVerified: isPreVerified,
  kycSubmittedAt: isPreVerified ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() : null,
  kycVerifiedAt: isPreVerified ? new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString() : null,
  };
});

// Ensure there is a dedicated admin login account
const adminExists = _users.some((u) => u.email === 'admin@imoney.my');
if (!adminExists) {
  _users.push({
    id: 'USR100',
    name: 'Admin User',
    email: 'admin@imoney.my',
    phone: '+60100000000',
    role: 'admin',
    referralCode: null,
    referralLevel: null,
    status: 'active',
    tokenBalance: 0,
    totalReferrals: 0,
    joinDate: '2022-01-01',
    password: 'admin123',
    referredBy: null,
    downline: [],
  });
}

// Make sure admin@imoney.my has the admin role and correct password
_users.forEach((u) => {
  if (u.email === 'admin@imoney.my') {
    u.role = 'admin';
    u.password = 'admin123';
  }
});

// Ensure at least one user of each role exists
const requiredRoles = ['admin', 'free_user', 'individual_agent', 'partner'];
requiredRoles.forEach((role) => {
  if (!_users.some((u) => u.role === role)) {
    const id = `USR-SYS-${role.toUpperCase()}`;
    _users.push({
      id,
      name: `Demo ${role.replace(/_/g, ' ')}`,
      email: `demo.${role}@imoney.my`,
      phone: '+60100000001',
      role,
      referralCode: role !== 'admin' ? `REF-DEMO-${role.substring(0, 3).toUpperCase()}` : null,
      referralLevel: role !== 'admin' ? 'Silver' : null,
      status: 'active',
      tokenBalance: 0,
      totalReferrals: 0,
      joinDate: '2025-01-01',
      password: 'demo123',
      referredBy: null,
      downline: [],
    });
  }
});

const _cases = deepClone(rawCases).map((c) => ({
  ...c,
  submittedBy: null,
}));

const _products = deepClone(rawProducts);
const _partners = deepClone(rawPartners);
const _commissionSchemes = deepClone(rawCommissionSchemes);
const _rewardConfig = deepClone(rawRewardConfig);
const _notifications = deepClone(rawNotifications);
const _campaigns = deepClone(rawCampaigns).map((c) => ({
  participantIds: [],
  ...c,
}));
// Seed some participant assignments for demo
_campaigns.forEach((c) => {
  if (c.status === 'active') {
    c.participantIds = ['USR002', 'PARTNER_1', 'PARTNER_2'];
  }
});
// Marketing materials are enriched with audience / dates / status / content / image
const _marketingMaterials = deepClone(rawMarketingMaterials).map((m) => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 90);
  const end = new Date(now);
  end.setDate(end.getDate() + 90);
  return {
    ...m,
    content: null,
    imageUrl: null, // data URL for uploaded promotional image (optional)
    audienceUserIds: '*', // '*' = all users; otherwise array of user IDs
    audienceRoles: [], // optional: ['free_user','individual_agent','partner']
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
    status: 'published', // draft | published
    shareCount: 0,
    createdBy: 'USR005',
    createdAt: start.toISOString(),
    updatedAt: start.toISOString(),
  };
});
const _productConfigs = deepClone(rawProductConfigs);
const _auditLogs = deepClone(rawAuditLogs);
const _redemptions = []; // Token redemptions are tracked here (in-memory)
const _tokenTransactions = []; // Token earning history (replaces relying on notifications)
const _rewardConfigs = []; // New array-based reward configs (per-product, 3-level)

// Invite links for self-registration (in-memory only)
const _invites = [
  {
    id: 'INV-0001',
    token: 'INV-abc123-PARTNER',
    role: 'partner',
    recipientEmail: 'newpartner@example.com',
    status: 'submitted', // sent | opened | submitted | approved | expired
    submittedData: {
      name: 'Chin Yee Ling',
      email: 'newpartner@example.com',
      phone: '+60129998888',
      icNumber: '900202034567',
      company: 'YeeLing Financial Services',
    },
    note: 'Referred by Lee Chong Wei',
    expiryDays: 14,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 11 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'USR005',
  },
  {
    id: 'INV-0002',
    token: 'INV-xyz789-AGENT',
    role: 'individual_agent',
    recipientEmail: 'prospect@example.com',
    status: 'sent',
    submittedData: null,
    note: 'From LinkedIn outreach',
    expiryDays: 7,
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    expiresAt: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'USR005',
  },
];

// ─── Seed reward configs (per-product, 3-level) ────────────────────
(function seedRewardConfigs() {
  const today = new Date();
  const toISO = (d) => d.toISOString().slice(0, 10);
  const addMonths = (d, m) => {
    const x = new Date(d);
    x.setMonth(x.getMonth() + m);
    return x;
  };
  // Default task bonus catalog — reused by all seed configs and as fallback
  const defaultTaskBonus = [
    { key: 'social_share', label: 'Share referral link to social media', tokens: 5, oneTime: true },
    { key: 'google_review', label: 'Leave a Google review', tokens: 2, oneTime: true },
    { key: 'ekyc_completed', label: 'Complete eKYC verification', tokens: 10, oneTime: true },
  ];

  const sample = [
    {
      productCode: 'IMY-PL-RHB-001',
      category: 'personal_loan',
      lenderName: 'RHB',
      productName: 'RHB Easy-Pinjaman Ekspres',
      selfApplication: { triggerStatus: 'disbursed', tokens: 50 },
      referralTiers: {
        level1: { triggerStatus: 'approved', tokens: 50 },
        level2: { triggerStatus: 'approved', tokens: 20 },
        level3: { triggerStatus: 'approved', tokens: 10 },
      },
      registrationBonus: { level1: 10, level2: 5, level3: 3 },
      taskBonus: defaultTaskBonus,
    },
    {
      productCode: 'IMY-CC-RHB-GOLD',
      category: 'credit_card',
      lenderName: 'RHB',
      productName: 'RHB Cashback Gold Mastercard',
      selfApplication: { triggerStatus: 'approved', tokens: 20 },
      referralTiers: {
        level1: { triggerStatus: 'approved', tokens: 30 },
        level2: { triggerStatus: 'approved', tokens: 15 },
        level3: { triggerStatus: 'approved', tokens: 8 },
      },
      registrationBonus: { level1: 10, level2: 5, level3: 3 },
      taskBonus: defaultTaskBonus,
    },
    {
      productCode: 'IMY-HL-MBSB-001',
      category: 'housing_loan',
      lenderName: 'MBSB',
      productName: 'MBSB Home Financing-i',
      selfApplication: { triggerStatus: 'disbursed', tokens: 100 },
      referralTiers: {
        level1: { triggerStatus: 'disbursed', tokens: 200 },
        level2: { triggerStatus: 'disbursed', tokens: 100 },
        level3: { triggerStatus: 'disbursed', tokens: 50 },
      },
      registrationBonus: { level1: 10, level2: 5, level3: 3 },
      taskBonus: defaultTaskBonus,
    },
  ];
  let idCounter = 0;
  sample.forEach((s) => {
    idCounter += 1;
    _rewardConfigs.push({
      id: `RC-${String(idCounter).padStart(4, '0')}`,
      ...s,
      effectiveFrom: toISO(addMonths(today, -3)),
      expiryDate: toISO(addMonths(today, 9)),
      approvalStatus: 'active',
      createdBy: 'USR005',
      createdAt: toISO(addMonths(today, -3)),
      updatedAt: toISO(addMonths(today, -3)),
      l1ApprovedBy: 'USR005',
      l1ApprovedAt: toISO(addMonths(today, -3)),
      l2ApprovedBy: 'USR010',
      l2ApprovedAt: toISO(addMonths(today, -3)),
    });
  });
})();

// ── ID & code generators ───────────────────────────────────────────
let _idCounters = {};

function generateId(prefix = 'ID') {
  if (!_idCounters[prefix]) {
    // Scan existing items to find the highest numeric suffix
    const allIds = [
      ..._users, ..._cases, ..._partners, ..._products,
      ..._notifications, ..._campaigns, ..._marketingMaterials,
    ]
      .map((item) => item.id)
      .filter((id) => id && id.startsWith(prefix));

    let max = 0;
    allIds.forEach((id) => {
      const num = parseInt(id.replace(/\D/g, ''), 10);
      if (!isNaN(num) && num > max) max = num;
    });
    _idCounters[prefix] = max;
  }
  _idCounters[prefix] += 1;
  return `${prefix}-${String(_idCounters[prefix]).padStart(3, '0')}`;
}

function generateReferralCode(name) {
  const initials = name
    .split(' ')
    .filter((w) => !['bin', 'binti', 'a/l', 'a/p'].includes(w.toLowerCase()))
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .substring(0, 3);
  const year = new Date().getFullYear();
  return `REF-${initials}${year}`;
}

// ── Database object ────────────────────────────────────────────────
const db = {
  // ─── Users ─────────────────────────────────────────────────────
  users: {
    getAll: () => [..._users],

    getById: (id) => _users.find((u) => u.id === id) || null,

    getByEmail: (email) =>
      _users.find((u) => u.email.toLowerCase() === email.toLowerCase()) || null,

    getByRole: (role) => _users.filter((u) => u.role === role),

    // Returns user IDs whose referredBy points to this partner. Falls back to all
    // individual_agent users if no explicit downline mapping exists (demo only).
    getDownlineUserIds: (partnerUserId) => {
      const direct = _users
        .filter((u) => u.referredBy === partnerUserId)
        .map((u) => u.id);
      if (direct.length > 0) return direct;
      return _users.filter((u) => u.role === 'individual_agent').map((u) => u.id);
    },

    // Returns 3-level downline tree as { level1: [...], level2: [...], level3: [...] }
    // BFS traversal of the referredBy graph (each level contains users referred by anyone in the previous level).
    getDownlineTree: (rootUserId, maxDepth = 3) => {
      const tree = { level1: [], level2: [], level3: [] };
      if (!rootUserId) return tree;
      const seen = new Set([rootUserId]);
      let frontier = [rootUserId];
      for (let depth = 1; depth <= maxDepth; depth += 1) {
        const next = [];
        _users.forEach((u) => {
          if (u.referredBy && frontier.includes(u.referredBy) && !seen.has(u.id)) {
            seen.add(u.id);
            tree[`level${depth}`].push(deepClone(u));
            next.push(u.id);
          }
        });
        frontier = next;
        if (frontier.length === 0) break;
      }
      return tree;
    },

    // Returns the productCodes a user has active commission entitlement for.
    // Used for partner product gating in the submission flow.
    getAssignedProductCodes: (userId) => {
      if (!userId) return [];
      const codes = new Set();
      _productConfigs.forEach((pc) => {
        if (pc.approvalStatus !== 'active') return;
        (pc.commissions || []).forEach((c) => {
          if (
            c.approvalStatus === 'active' &&
            (c.recipientId === userId || c.specificAgentId === userId) &&
            (!c.effectiveTo || new Date(c.effectiveTo) > new Date())
          ) {
            codes.add(pc.productCode);
          }
        });
      });
      return Array.from(codes);
    },

    authenticate: (email, password) => {
      const user = _users.find(
        (u) =>
          u.email.toLowerCase() === email.toLowerCase() &&
          u.password === password
      );
      if (!user) return null;
      // Return a copy without the password
      const { password: _pw, ...safeUser } = user;
      return safeUser;
    },

    create: (userData) => {
      const id = generateId('USR');
      const newUser = {
        id,
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        role: userData.role || 'free_user',
        referralCode: userData.referralCode || generateReferralCode(userData.name || 'User'),
        referralLevel: userData.referralLevel || 'Silver',
        status: userData.status || 'active',
        tokenBalance: userData.tokenBalance || 0,
        totalReferrals: userData.totalReferrals || 0,
        joinDate: userData.joinDate || new Date().toISOString().split('T')[0],
        password: userData.password || 'demo123',
        referredBy: userData.referredBy || null,
        downline: userData.downline || [],
        ...userData,
        id, // ensure auto-generated id is not overridden
      };
      _users.push(newUser);
      return deepClone(newUser);
    },

    update: (id, fields) => {
      const idx = _users.findIndex((u) => u.id === id);
      if (idx === -1) return null;
      _users[idx] = { ..._users[idx], ...fields, id }; // protect id from being changed
      return deepClone(_users[idx]);
    },

    delete: (id) => {
      const idx = _users.findIndex((u) => u.id === id);
      if (idx === -1) return false;
      _users.splice(idx, 1);
      return true;
    },

    // Atomic token balance adjust (delta can be negative for deduction)
    adjustTokenBalance: (id, delta) => {
      const idx = _users.findIndex((u) => u.id === id);
      if (idx === -1) return null;
      const current = Number(_users[idx].tokenBalance) || 0;
      _users[idx].tokenBalance = Math.max(0, current + Number(delta));
      return deepClone(_users[idx]);
    },
  },

  // ─── Cases ─────────────────────────────────────────────────────
  cases: {
    getAll: () => deepClone(_cases),

    getByUserId: (userId) =>
      deepClone(
        _cases.filter(
          (c) => c.applicantId === userId || c.referralOwnerId === userId
        )
      ),

    getByReferralOwnerId: (userId) =>
      deepClone(_cases.filter((c) => c.referralOwnerId === userId)),

    // Returns own cases + (optionally) cases owned by downline users
    getForPartner: (partnerUserId, includeDownline = true) => {
      if (!includeDownline) {
        return deepClone(_cases.filter((c) => c.referralOwnerId === partnerUserId));
      }
      // Find downline user IDs
      const downlineIds = _users
        .filter((u) => u.referredBy === partnerUserId)
        .map((u) => u.id);
      // If no explicit downline relationships exist, fall back to a sample mapping
      // for demo purposes (every individual_agent is treated as downline of every partner)
      let effectiveDownline = downlineIds;
      if (effectiveDownline.length === 0) {
        effectiveDownline = _users
          .filter((u) => u.role === 'individual_agent')
          .map((u) => u.id);
      }
      const allOwnerIds = new Set([partnerUserId, ...effectiveDownline]);
      return deepClone(_cases.filter((c) => allOwnerIds.has(c.referralOwnerId)));
    },

    getByStatus: (status) => deepClone(_cases.filter((c) => c.status === status)),

    getInDateRange: (startDate, endDate) => {
      const start = startDate ? new Date(startDate).getTime() : -Infinity;
      const end = endDate ? new Date(endDate).getTime() : Infinity;
      return deepClone(
        _cases.filter((c) => {
          const t = new Date(c.createdAt).getTime();
          return t >= start && t <= end;
        })
      );
    },

    create: (caseData) => {
      const id = generateId('CASE');
      const newCase = {
        id,
        submittedBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        commissionAmount: 0,
        commissionStatus: 'pending',
        ...caseData,
        id,
      };
      _cases.push(newCase);
      return deepClone(newCase);
    },

    updateStatus: (id, newStatus) => {
      const idx = _cases.findIndex((c) => c.id === id);
      if (idx === -1) return null;
      _cases[idx].status = newStatus;
      _cases[idx].updatedAt = new Date().toISOString();
      return deepClone(_cases[idx]);
    },

    // General field update (for commissionAmount, commissionStatus, etc.)
    update: (id, fields) => {
      const idx = _cases.findIndex((c) => c.id === id);
      if (idx === -1) return null;
      _cases[idx] = { ..._cases[idx], ...fields, id, updatedAt: new Date().toISOString() };
      return deepClone(_cases[idx]);
    },
  },

  // ─── Partners ──────────────────────────────────────────────────
  partners: {
    getAll: () => [..._partners],

    getById: (id) => _partners.find((p) => p.id === id) || null,

    create: (data) => {
      const id = generateId('PTR');
      const newPartner = { id, ...data, id };
      _partners.push(newPartner);
      return deepClone(newPartner);
    },

    update: (id, fields) => {
      const idx = _partners.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      _partners[idx] = { ..._partners[idx], ...fields, id };
      return deepClone(_partners[idx]);
    },
  },

  // ─── Products ──────────────────────────────────────────────────
  products: {
    getAll: () => [..._products],
  },

  // ─── Commission Schemes ────────────────────────────────────────
  commissionSchemes: {
    getAll: () => [..._commissionSchemes],
  },

  // ─── Notifications ─────────────────────────────────────────────
  notifications: {
    getAll: () => [..._notifications],

    getByUserId: (userId) => _notifications.filter((n) => n.userId === userId),

    create: (data) => {
      const id = generateId('NOTIF');
      const newNotif = {
        id,
        createdAt: new Date().toISOString(),
        read: false,
        ...data,
        id,
      };
      _notifications.push(newNotif);
      return deepClone(newNotif);
    },

    markRead: (id) => {
      const idx = _notifications.findIndex((n) => n.id === id);
      if (idx === -1) return null;
      _notifications[idx].read = true;
      return deepClone(_notifications[idx]);
    },
  },

  // ─── Campaigns ─────────────────────────────────────────────────
  campaigns: {
    getAll: () => deepClone(_campaigns),
    getById: (id) => {
      const c = _campaigns.find((c) => c.id === id);
      return c ? deepClone(c) : null;
    },
    update: (id, fields) => {
      const idx = _campaigns.findIndex((c) => c.id === id);
      if (idx === -1) return null;
      Object.assign(_campaigns[idx], fields);
      return deepClone(_campaigns[idx]);
    },
    create: (data) => {
      const newCampaign = {
        id: generateId('CAMP'),
        participantIds: [],
        ...data,
      };
      _campaigns.push(newCampaign);
      return deepClone(newCampaign);
    },
  },

  // ─── Marketing Materials ───────────────────────────────────────
  // Full CRUD with audience filtering, date range, and status.
  marketingMaterials: {
    getAll: () => deepClone(_marketingMaterials),

    // Admin view — adds computedStatus (expired if endDate < now)
    getAllEnriched: () => {
      const now = new Date();
      return deepClone(_marketingMaterials).map((m) => {
        const expired = m.endDate && new Date(m.endDate) < now;
        return {
          ...m,
          computedStatus: expired && m.status === 'published' ? 'expired' : m.status,
        };
      });
    },

    getById: (id) => {
      const m = _marketingMaterials.find((x) => x.id === id);
      return m ? deepClone(m) : null;
    },

    // Partner / agent / free user view — only published, in-date, and audience-matching materials.
    // Audience matches if ANY of:
    //   (a) audienceUserIds === '*' (everyone)
    //   (b) audienceUserIds array includes userId
    //   (c) audienceRoles array includes the user's role
    getForUser: (userId, asOfDate = new Date()) => {
      const now = asOfDate instanceof Date ? asOfDate : new Date(asOfDate);
      const user = _users.find((u) => u.id === userId);
      const userRole = user?.role;
      return deepClone(
        _marketingMaterials.filter((m) => {
          if (m.status !== 'published') return false;
          if (m.startDate && new Date(m.startDate) > now) return false;
          if (m.endDate && new Date(m.endDate) < now) return false;
          // Audience match
          if (m.audienceUserIds === '*') return true;
          if (Array.isArray(m.audienceUserIds) && m.audienceUserIds.includes(userId)) return true;
          if (userRole && Array.isArray(m.audienceRoles) && m.audienceRoles.includes(userRole)) return true;
          return false;
        })
      );
    },

    create: (data) => {
      const id = generateId('MAT');
      const now = new Date().toISOString();
      const newMaterial = {
        id,
        name: data.name || 'Untitled',
        type: data.type || 'brochure',
        product: data.product || null,
        fileSize: data.fileSize || '-',
        thumbnail: data.thumbnail || null,
        imageUrl: data.imageUrl || null,
        downloadCount: 0,
        shareCount: 0,
        content: data.content || null,
        audienceUserIds: data.audienceUserIds || '*',
        audienceRoles: Array.isArray(data.audienceRoles) ? data.audienceRoles : [],
        startDate: data.startDate || now.slice(0, 10),
        endDate: data.endDate || null,
        status: data.status || 'draft',
        createdBy: data.createdBy || 'SYSTEM',
        createdAt: now,
        updatedAt: now,
      };
      _marketingMaterials.push(newMaterial);
      return deepClone(newMaterial);
    },

    update: (id, fields) => {
      const idx = _marketingMaterials.findIndex((m) => m.id === id);
      if (idx === -1) return null;
      _marketingMaterials[idx] = {
        ..._marketingMaterials[idx],
        ...fields,
        id,
        updatedAt: new Date().toISOString(),
      };
      return deepClone(_marketingMaterials[idx]);
    },

    delete: (id) => {
      const idx = _marketingMaterials.findIndex((m) => m.id === id);
      if (idx === -1) return false;
      _marketingMaterials.splice(idx, 1);
      return true;
    },

    incrementShareCount: (id) => {
      const idx = _marketingMaterials.findIndex((m) => m.id === id);
      if (idx === -1) return null;
      _marketingMaterials[idx].shareCount = (_marketingMaterials[idx].shareCount || 0) + 1;
      return deepClone(_marketingMaterials[idx]);
    },

    incrementDownloadCount: (id) => {
      const idx = _marketingMaterials.findIndex((m) => m.id === id);
      if (idx === -1) return null;
      _marketingMaterials[idx].downloadCount = (_marketingMaterials[idx].downloadCount || 0) + 1;
      return deepClone(_marketingMaterials[idx]);
    },
  },

  // ─── Token Transactions ────────────────────────────────────────
  tokenTransactions: {
    getAll: () => deepClone(_tokenTransactions),

    getByUserId: (userId) =>
      deepClone(_tokenTransactions.filter((t) => t.userId === userId))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),

    // Returns only transactions in escrow (pending KYC) for a user
    getPendingByUserId: (userId) =>
      deepClone(_tokenTransactions.filter((t) => t.userId === userId && t.status === 'pending_kyc'))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),

    getByDateRange: (startDate, endDate, userId = null) => {
      const startMs = startDate ? new Date(startDate).getTime() : -Infinity;
      const endMs = endDate ? new Date(endDate).getTime() : Infinity;
      return deepClone(
        _tokenTransactions.filter((t) => {
          const tMs = new Date(t.createdAt).getTime();
          if (tMs < startMs || tMs > endMs) return false;
          if (userId && t.userId !== userId) return false;
          return true;
        })
      );
    },

    create: (data) => {
      const id = generateId('TXN');
      const newTxn = {
        id,
        userId: data.userId,
        amount: Number(data.amount) || 0,
        type: data.type || 'reward', // reward | redemption | bonus | adjustment | task_bonus
        source: data.source || '',   // free-text source description
        caseId: data.caseId || null,
        level: data.level || null,   // for referral chain awards: 1, 2, or 3
        productCode: data.productCode || null,
        status: data.status || 'released', // released | pending_kyc
        createdAt: new Date().toISOString(),
        ...data,
        id,
      };
      _tokenTransactions.push(newTxn);
      return deepClone(newTxn);
    },

    // Used by db.engine.releasePendingRewards — flips a pending txn to released
    release: (id) => {
      const idx = _tokenTransactions.findIndex((t) => t.id === id);
      if (idx === -1) return null;
      _tokenTransactions[idx].status = 'released';
      _tokenTransactions[idx].releasedAt = new Date().toISOString();
      return deepClone(_tokenTransactions[idx]);
    },
  },

  // ─── Reward Configs (per-product, 3-level structure) ──────────
  // Mirrors db.productConfigs CRUD pattern
  rewardConfigs: {
    getAll: () => deepClone(_rewardConfigs).map((rc) => {
      const expired = rc.expiryDate && new Date(rc.expiryDate) < new Date();
      const computedStatus = rc.approvalStatus === 'active' && expired ? 'expired' : rc.approvalStatus;
      return { ...rc, computedStatus };
    }),

    getById: (id) => {
      const rc = _rewardConfigs.find((x) => x.id === id);
      return rc ? deepClone(rc) : null;
    },

    getByCategory: (category) =>
      deepClone(_rewardConfigs.filter((rc) => rc.category === category)),

    getActive: () =>
      deepClone(_rewardConfigs.filter((rc) => {
        if (rc.approvalStatus !== 'active') return false;
        if (rc.expiryDate && new Date(rc.expiryDate) < new Date()) return false;
        return true;
      })),

    // Find the first active reward config matching a productCode or category fallback
    findForProduct: (productCode, category) => {
      const now = new Date();
      const isActive = (rc) =>
        rc.approvalStatus === 'active' && (!rc.expiryDate || new Date(rc.expiryDate) >= now);
      const exact = _rewardConfigs.find(
        (rc) => isActive(rc) && rc.productCode === productCode
      );
      if (exact) return deepClone(exact);
      const byCategory = _rewardConfigs.find(
        (rc) => isActive(rc) && rc.category === category
      );
      return byCategory ? deepClone(byCategory) : null;
    },

    create: (data) => {
      const id = generateId('RC');
      const now = new Date().toISOString();
      const newConfig = {
        id,
        productCode: data.productCode,
        category: data.category,
        lenderName: data.lenderName,
        productName: data.productName,
        effectiveFrom: data.effectiveFrom || now.slice(0, 10),
        expiryDate: data.expiryDate || null,
        approvalStatus: 'pending_l1',
        // New trigger-per-tier model
        selfApplication: data.selfApplication || { triggerStatus: 'disbursed', tokens: 0 },
        referralTiers: data.referralTiers || {
          level1: { triggerStatus: 'approved', tokens: 0 },
          level2: { triggerStatus: 'approved', tokens: 0 },
          level3: { triggerStatus: 'approved', tokens: 0 },
        },
        registrationBonus: data.registrationBonus || { level1: 0, level2: 0, level3: 0 },
        taskBonus: data.taskBonus || [
          { key: 'social_share', label: 'Share referral link to social media', tokens: 5, oneTime: true },
          { key: 'google_review', label: 'Leave a Google review', tokens: 2, oneTime: true },
          { key: 'ekyc_completed', label: 'Complete eKYC verification', tokens: 10, oneTime: true },
        ],
        l1ApprovedBy: null,
        l1ApprovedAt: null,
        l2ApprovedBy: null,
        l2ApprovedAt: null,
        createdBy: data.createdBy || 'SYSTEM',
        createdAt: now,
        updatedAt: now,
      };
      _rewardConfigs.push(newConfig);
      return deepClone(newConfig);
    },

    // Returns the task bonus catalog from the first active reward config.
    // Used by UserProfile and db.engine.awardTaskBonus.
    getActiveTaskBonuses: () => {
      const active = _rewardConfigs.find((rc) => {
        if (rc.approvalStatus !== 'active') return false;
        if (rc.expiryDate && new Date(rc.expiryDate) < new Date()) return false;
        return Array.isArray(rc.taskBonus) && rc.taskBonus.length > 0;
      });
      return active?.taskBonus ? deepClone(active.taskBonus) : [];
    },

    update: (id, fields) => {
      const idx = _rewardConfigs.findIndex((rc) => rc.id === id);
      if (idx === -1) return null;
      _rewardConfigs[idx] = {
        ..._rewardConfigs[idx],
        ...fields,
        id,
        updatedAt: new Date().toISOString(),
      };
      return deepClone(_rewardConfigs[idx]);
    },

    approveL1: (id, approverId) => {
      const idx = _rewardConfigs.findIndex((rc) => rc.id === id);
      if (idx === -1) return null;
      _rewardConfigs[idx].approvalStatus = 'pending_l2';
      _rewardConfigs[idx].l1ApprovedBy = approverId;
      _rewardConfigs[idx].l1ApprovedAt = new Date().toISOString();
      _rewardConfigs[idx].updatedAt = new Date().toISOString();
      return deepClone(_rewardConfigs[idx]);
    },

    approveL2: (id, approverId) => {
      const idx = _rewardConfigs.findIndex((rc) => rc.id === id);
      if (idx === -1) return null;
      _rewardConfigs[idx].approvalStatus = 'active';
      _rewardConfigs[idx].l2ApprovedBy = approverId;
      _rewardConfigs[idx].l2ApprovedAt = new Date().toISOString();
      _rewardConfigs[idx].updatedAt = new Date().toISOString();
      return deepClone(_rewardConfigs[idx]);
    },

    reject: (id, reason) => {
      const idx = _rewardConfigs.findIndex((rc) => rc.id === id);
      if (idx === -1) return null;
      _rewardConfigs[idx].approvalStatus = 'rejected';
      _rewardConfigs[idx].rejectionReason = reason;
      _rewardConfigs[idx].updatedAt = new Date().toISOString();
      return deepClone(_rewardConfigs[idx]);
    },

    delete: (id) => {
      const idx = _rewardConfigs.findIndex((rc) => rc.id === id);
      if (idx === -1) return false;
      _rewardConfigs.splice(idx, 1);
      return true;
    },
  },

  // ─── Product Configs (unified product + commission) ───────────
  productConfigs: {
    getAll: () => deepClone(_productConfigs).map((pc) => ({
      ...pc,
      computedStatus: computeContractStatus(pc.contractExpireDate, pc.approvalStatus),
    })),

    getById: (id) => {
      const pc = _productConfigs.find((p) => p.id === id);
      if (!pc) return null;
      const copy = deepClone(pc);
      copy.computedStatus = computeContractStatus(pc.contractExpireDate, pc.approvalStatus);
      return copy;
    },

    getByCategory: (category) =>
      deepClone(_productConfigs)
        .filter((pc) => pc.category === category)
        .map((pc) => ({
          ...pc,
          computedStatus: computeContractStatus(pc.contractExpireDate, pc.approvalStatus),
        })),

    getByStatus: (status) =>
      deepClone(_productConfigs)
        .filter((pc) => computeContractStatus(pc.contractExpireDate, pc.approvalStatus) === status)
        .map((pc) => ({
          ...pc,
          computedStatus: status,
        })),

    getExpiringSoon: (days = 30) => {
      const now = new Date();
      return deepClone(_productConfigs)
        .filter((pc) => {
          const expire = new Date(pc.contractExpireDate);
          const daysLeft = Math.floor((expire - now) / (1000 * 60 * 60 * 24));
          return daysLeft >= 0 && daysLeft <= days && pc.approvalStatus === 'active';
        })
        .map((pc) => ({
          ...pc,
          computedStatus: 'expiring_soon',
        }));
    },

    getPendingApproval: () =>
      deepClone(_productConfigs)
        .filter((pc) => pc.approvalStatus === 'pending_l1' || pc.approvalStatus === 'pending_l2')
        .map((pc) => ({
          ...pc,
          computedStatus: pc.approvalStatus,
        })),

    create: (configData) => {
      const id = generateId('PC');
      const now = new Date().toISOString();
      const newConfig = {
        id,
        productCode: configData.productCode,
        category: configData.category,
        lenderName: configData.lenderName,
        productName: configData.productName,
        contractStartDate: configData.contractStartDate,
        contractExpireDate: configData.contractExpireDate,
        contractDuration: configData.contractDuration || 12,
        approvalStatus: 'pending_l1',
        commissions: configData.commissions || [],
        createdBy: configData.createdBy || 'SYSTEM',
        createdAt: now,
        updatedAt: now,
      };
      _productConfigs.push(newConfig);
      return deepClone(newConfig);
    },

    update: (id, fields) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      _productConfigs[idx] = {
        ..._productConfigs[idx],
        ...fields,
        id: _productConfigs[idx].id,
        updatedAt: new Date().toISOString(),
      };
      return deepClone(_productConfigs[idx]);
    },

    delete: (id) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return false;
      _productConfigs.splice(idx, 1);
      return true;
    },

    approveL1: (id, approverId) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      const now = new Date().toISOString();
      _productConfigs[idx].approvalStatus = 'pending_l2';
      _productConfigs[idx].commissions.forEach((c) => {
        if (c.approvalStatus === 'pending_l1') {
          c.approvalStatus = 'pending_l2';
          c.l1ApprovedBy = approverId;
          c.l1ApprovedAt = now;
        }
      });
      _productConfigs[idx].updatedAt = now;
      return deepClone(_productConfigs[idx]);
    },

    approveL2: (id, approverId) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      const now = new Date();
      const nowIso = now.toISOString();
      const today = now.toISOString().slice(0, 10);
      _productConfigs[idx].approvalStatus = 'active';

      const commissions = _productConfigs[idx].commissions;
      commissions.forEach((c) => {
        if (c.approvalStatus === 'pending_l2') {
          c.approvalStatus = 'active';
          c.l2ApprovedBy = approverId;
          c.l2ApprovedAt = nowIso;
          // Effective date is the actual approval date
          c.effectiveFrom = today;

          // If this commission supersedes an older one, close out the old entry
          if (c._supersedes) {
            const old = commissions.find((oc) => oc.id === c._supersedes);
            if (old) {
              old.effectiveTo = today;
              old.supersededBy = c.id;
            }
            delete c._supersedes;
          }
        }
      });
      _productConfigs[idx].updatedAt = nowIso;
      return deepClone(_productConfigs[idx]);
    },

    reject: (id, reason) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      const now = new Date().toISOString();
      _productConfigs[idx].approvalStatus = 'rejected';
      _productConfigs[idx].rejectionReason = reason;
      _productConfigs[idx].updatedAt = now;
      return deepClone(_productConfigs[idx]);
    },

    renew: (id, durationMonths) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      const now = new Date();
      const newExpire = new Date(now);
      newExpire.setMonth(newExpire.getMonth() + durationMonths);
      _productConfigs[idx].contractStartDate = now.toISOString().slice(0, 10);
      _productConfigs[idx].contractExpireDate = newExpire.toISOString().slice(0, 10);
      _productConfigs[idx].contractDuration = durationMonths;
      _productConfigs[idx].approvalStatus = 'pending_l1';
      _productConfigs[idx].updatedAt = now.toISOString();
      return deepClone(_productConfigs[idx]);
    },

    togglePause: (id) => {
      const idx = _productConfigs.findIndex((p) => p.id === id);
      if (idx === -1) return null;
      _productConfigs[idx].approvalStatus =
        _productConfigs[idx].approvalStatus === 'paused' ? 'active' : 'paused';
      _productConfigs[idx].updatedAt = new Date().toISOString();
      return deepClone(_productConfigs[idx]);
    },

    addCommission: (productId, commissionData) => {
      const idx = _productConfigs.findIndex((p) => p.id === productId);
      if (idx === -1) return null;
      if (!commissionData.recipientId) {
        throw new Error('addCommission requires a recipientId');
      }

      // Look up recipient to denormalize name & role
      const recipient = _users.find((u) => u.id === commissionData.recipientId);
      const now = new Date();
      const today = now.toISOString().slice(0, 10);

      const newCommission = {
        id: generateId('CM'),
        agentType: commissionData.agentType || recipient?.role || 'partner',
        recipientId: commissionData.recipientId,
        recipientName: commissionData.recipientName || recipient?.name || 'Unknown',
        recipientRole: recipient?.role || commissionData.agentType || 'partner',
        commissionType: commissionData.commissionType,
        perCaseAmount: commissionData.commissionType === 'per_case' ? commissionData.perCaseAmount : null,
        tiers: commissionData.commissionType === 'percentage' ? (commissionData.tiers || []) : [],
        effectiveFrom: commissionData.effectiveFrom || today,
        effectiveTo: null,
        supersededBy: null,
        approvalStatus: 'pending_l1',
        l1ApprovedBy: null,
        l1ApprovedAt: null,
        l2ApprovedBy: null,
        l2ApprovedAt: null,
        createdBy: commissionData.createdBy || 'SYSTEM',
        createdAt: now.toISOString(),
      };
      _productConfigs[idx].commissions.push(newCommission);
      _productConfigs[idx].approvalStatus = 'pending_l1';
      _productConfigs[idx].updatedAt = now.toISOString();
      return deepClone(newCommission);
    },

    /**
     * Supersede an existing commission with a new one.
     * - Marks the old commission's effectiveTo = today and sets supersededBy.
     * - Creates a new commission for the same recipient with effectiveFrom = today.
     * - Old commission stays in the array so historical cases can still compute against it.
     */
    supersedeCommission: (productId, oldCommissionId, newCommissionData) => {
      const idx = _productConfigs.findIndex((p) => p.id === productId);
      if (idx === -1) return null;
      const oldIdx = _productConfigs[idx].commissions.findIndex((c) => c.id === oldCommissionId);
      if (oldIdx === -1) return null;
      const old = _productConfigs[idx].commissions[oldIdx];

      const now = new Date();
      const today = now.toISOString().slice(0, 10);

      // Create the new commission
      const newCommission = {
        id: generateId('CM'),
        agentType: old.agentType,
        recipientId: old.recipientId,
        recipientName: old.recipientName,
        recipientRole: old.recipientRole,
        commissionType: newCommissionData.commissionType,
        perCaseAmount: newCommissionData.commissionType === 'per_case' ? newCommissionData.perCaseAmount : null,
        tiers: newCommissionData.commissionType === 'percentage' ? (newCommissionData.tiers || []) : [],
        effectiveFrom: today,
        effectiveTo: null,
        supersededBy: null,
        approvalStatus: 'pending_l1',
        l1ApprovedBy: null,
        l1ApprovedAt: null,
        l2ApprovedBy: null,
        l2ApprovedAt: null,
        createdBy: newCommissionData.createdBy || 'SYSTEM',
        createdAt: now.toISOString(),
      };

      // Don't close out the old commission yet - only do that when L2 approval happens.
      // Store a reference so we can close the loop on approval
      newCommission._supersedes = oldCommissionId;

      _productConfigs[idx].commissions.push(newCommission);
      _productConfigs[idx].approvalStatus = 'pending_l1';
      _productConfigs[idx].updatedAt = now.toISOString();
      return deepClone(newCommission);
    },

    /**
     * Returns the commissions that were/are active on a given date (for case commission lookup).
     * Filters out superseded entries (effectiveTo <= asOfDate) and pending entries.
     */
    getActiveCommissionsByDate: (productId, asOfDate) => {
      const pc = _productConfigs.find((p) => p.id === productId);
      if (!pc) return [];
      const d = new Date(asOfDate);
      return deepClone(
        pc.commissions.filter((c) => {
          if (c.approvalStatus !== 'active') return false;
          const from = new Date(c.effectiveFrom);
          if (from > d) return false;
          if (c.effectiveTo && new Date(c.effectiveTo) <= d) return false;
          return true;
        })
      );
    },
  },

  // ─── Audit Logs ────────────────────────────────────────────────
  auditLogs: {
    getAll: () => deepClone(_auditLogs).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),

    getByEntity: (entityType, entityId) =>
      deepClone(_auditLogs)
        .filter((l) => l.entityType === entityType && l.entityId === entityId)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),

    getByUser: (userId) =>
      deepClone(_auditLogs)
        .filter((l) => l.userId === userId)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),

    create: (logData) => {
      const id = generateId('LOG');
      const newLog = {
        id,
        timestamp: new Date().toISOString(),
        userId: logData.userId || 'SYSTEM',
        userName: logData.userName || 'System',
        userRole: logData.userRole || 'admin',
        action: logData.action,
        entityType: logData.entityType,
        entityId: logData.entityId,
        description: logData.description || '',
        reason: logData.reason || '',
        changes: logData.changes || null,
      };
      _auditLogs.push(newLog);
      return deepClone(newLog);
    },
  },

  // ─── Invite Links (for self-registration flow) ────────────────
  invites: {
    getAll: () =>
      deepClone(_invites).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),

    getById: (id) => {
      const found = _invites.find((i) => i.id === id);
      return found ? deepClone(found) : null;
    },

    getByToken: (token) => {
      const found = _invites.find((i) => i.token === token);
      return found ? deepClone(found) : null;
    },

    create: (data) => {
      const id = generateId('INV');
      // Random alphanumeric token
      const tokenPart = Math.random().toString(36).slice(2, 10);
      const token = `INV-${tokenPart}-${(data.role || 'user').slice(0, 6).toUpperCase()}`;
      const now = new Date();
      const expiresAt = new Date(now);
      expiresAt.setDate(expiresAt.getDate() + (data.expiryDays || 14));
      const newInvite = {
        id,
        token,
        role: data.role || 'individual_agent',
        recipientEmail: data.recipientEmail || '',
        status: 'sent',
        submittedData: null,
        note: data.note || '',
        expiryDays: data.expiryDays || 14,
        createdAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        createdBy: data.createdBy || 'SYSTEM',
      };
      _invites.push(newInvite);
      return deepClone(newInvite);
    },

    update: (id, fields) => {
      const idx = _invites.findIndex((i) => i.id === id);
      if (idx === -1) return null;
      _invites[idx] = { ..._invites[idx], ...fields, id: _invites[idx].id };
      return deepClone(_invites[idx]);
    },

    // Simulate the prospective user opening the link and submitting the form
    simulateSubmission: (id, submittedData) => {
      const idx = _invites.findIndex((i) => i.id === id);
      if (idx === -1) return null;
      _invites[idx].status = 'submitted';
      _invites[idx].submittedData = submittedData || {
        name: 'Demo Applicant',
        email: _invites[idx].recipientEmail || 'applicant@example.com',
        phone: '+60120000000',
        icNumber: '900101011234',
        company: 'Demo Company',
      };
      return deepClone(_invites[idx]);
    },

    // Admin approves the submitted invite → creates an actual user account
    approve: (id) => {
      const idx = _invites.findIndex((i) => i.id === id);
      if (idx === -1) return null;
      const invite = _invites[idx];
      if (invite.status !== 'submitted') return null;
      const data = invite.submittedData || {};
      const newUser = {
        id: generateId('USR'),
        name: data.name || 'New Member',
        email: data.email || '',
        phone: data.phone || '',
        role: invite.role,
        referralCode: `REF-${(data.name || 'MEMBER').replace(/\s/g, '').slice(0, 6).toUpperCase()}${Date.now().toString().slice(-3)}`,
        referralLevel: invite.role === 'free_user' ? 'Silver' : null,
        status: 'active',
        tokenBalance: 0,
        totalReferrals: 0,
        joinDate: new Date().toISOString().slice(0, 10),
        password: 'temp123',
        referredBy: null,
        downline: [],
        icNumber: data.icNumber || '',
        company: data.company || '',
      };
      _users.push(newUser);
      _invites[idx].status = 'approved';
      _invites[idx].approvedAt = new Date().toISOString();
      _invites[idx].approvedUserId = newUser.id;
      return { invite: deepClone(_invites[idx]), user: deepClone(newUser) };
    },

    delete: (id) => {
      const idx = _invites.findIndex((i) => i.id === id);
      if (idx === -1) return false;
      _invites.splice(idx, 1);
      return true;
    },
  },

  // ─── Redemptions (token redemption history) ────────────────────
  redemptions: {
    getAll: () => deepClone(_redemptions),

    getByUserId: (userId) =>
      deepClone(_redemptions.filter((r) => r.userId === userId))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),

    create: (data) => {
      const id = generateId('RDM');
      const newRedemption = {
        id,
        userId: data.userId,
        itemKey: data.itemKey,
        itemName: data.itemName,
        tokensSpent: Number(data.tokensSpent) || 0,
        status: data.status || 'completed',
        createdAt: new Date().toISOString(),
        ...data,
        id,
      };
      _redemptions.push(newRedemption);
      return deepClone(newRedemption);
    },
  },

  // ─── Effect Cascade Engine ─────────────────────────────────────
  // Cross-module side-effects so pages don't have to know which downstream
  // updates to trigger. Pages call one engine method; the engine handles the
  // commission calculation, token rewards, notifications, and audit logging.
  engine: {
    /**
     * Create a notification with consistent shape.
     */
    notify(userId, type, title, message, metadata = {}) {
      const id = generateId('NTF');
      const notif = {
        id,
        userId,
        type,
        title,
        message,
        read: false,
        createdAt: new Date().toISOString(),
        ...metadata,
      };
      _notifications.push(notif);
      return deepClone(notif);
    },

    /**
     * Look up the reward config tier for a given product type and award status.
     * Returns the token amount (number).
     */
    _getRewardTokens(productCategory, isReferral, status) {
      // Map productConfig category → mockData rewardConfig key
      const map = {
        personal_loan: 'personal_financing',
        housing_loan: 'housing_loan',
        credit_card: 'credit_card',
        property: 'property',
        business_loan: 'personal_financing',
        government_loan: 'personal_financing',
        micro_loan: 'personal_financing',
      };
      const key = map[productCategory] || 'personal_financing';
      const config = _rewardConfig[key];
      if (!config) return 0;
      const bucket = isReferral ? config.referralApplication : config.selfApplication;
      const statusKey = String(status || '').toLowerCase().replace(/\s+/g, '_');
      // statusKey: submitted | approved | disbursed
      return Number(bucket?.[statusKey]) || 0;
    },

    /**
     * Award tokens to applicant + referral owner chain (3 levels deep) for a case milestone.
     * Triggered on case creation (submitted) and on Approved/Disbursed transitions.
     *
     * NEW TRIGGER MODEL:
     *   Each reward config tier has { triggerStatus, tokens } — tokens fire only when
     *   the case reaches the configured trigger. E.g., if selfApplication.triggerStatus
     *   is 'disbursed', the user only earns when their case is Disbursed, not Submitted.
     *
     * KYC ESCROW:
     *   If the recipient's kycStatus is not 'verified', the transaction is still created
     *   with status 'pending_kyc' but the tokenBalance is NOT increased. Calling
     *   releasePendingRewards(userId) after verification releases all held transactions.
     */
    awardTokens(caseRecord, status) {
      const category = mapProductToCategory(caseRecord);
      const results = [];
      const statusKey = String(status || '').toLowerCase().replace(/\s+/g, '_');
      const newConfig = db.rewardConfigs.findForProduct(caseRecord.productCode, category);

      // Helper: does a tier config fire at this case status?
      const fires = (tier) => {
        if (!tier) return false;
        // New shape: { triggerStatus, tokens }
        if (typeof tier.triggerStatus === 'string') {
          return tier.triggerStatus === statusKey && Number(tier.tokens) > 0;
        }
        // Legacy shape (backward compat): { submitted, approved, disbursed }
        return Number(tier[statusKey]) > 0;
      };
      const amountOf = (tier) => {
        if (!tier) return 0;
        if (typeof tier.triggerStatus === 'string') return Number(tier.tokens) || 0;
        return Number(tier[statusKey]) || 0;
      };

      const credit = (recipientUser, amount, kind, level = null) => {
        if (!amount || amount <= 0) return;
        const isVerified = recipientUser.kycStatus === 'verified';
        const txnStatus = isVerified ? 'released' : 'pending_kyc';

        // Only adjust balance if verified
        if (isVerified) {
          db.users.adjustTokenBalance(recipientUser.id, amount);
        }

        db.tokenTransactions.create({
          userId: recipientUser.id,
          amount,
          type: 'reward',
          source: kind === 'self'
            ? `${caseRecord.productName} self application (${status})`
            : `${caseRecord.productName} L${level} referral (${status})`,
          caseId: caseRecord.id,
          level,
          productCode: caseRecord.productCode || null,
          status: txnStatus,
        });

        db.engine.notify(
          recipientUser.id,
          'token',
          isVerified ? `+${amount} tokens credited` : `+${amount} tokens pending KYC`,
          kind === 'self'
            ? `Your ${caseRecord.productName} application reached ${status}. ${amount} tokens ${isVerified ? 'added' : 'held pending KYC verification'}.`
            : `L${level} referral ${caseRecord.applicantName}'s ${caseRecord.productName} reached ${status}. ${amount} tokens ${isVerified ? 'added' : 'held pending KYC verification'}.`,
          { caseId: caseRecord.id, amount, level, kind, escrowed: !isVerified }
        );
        results.push({ recipientId: recipientUser.id, amount, kind, level, escrowed: !isVerified });
      };

      // ── Self application reward ──────────────────────────────
      const applicant = _users.find((u) => u.id === caseRecord.applicantId);
      if (applicant && applicant.role === 'free_user') {
        const selfTier = newConfig?.selfApplication;
        if (fires(selfTier)) {
          credit(applicant, amountOf(selfTier), 'self');
        } else if (!newConfig) {
          // Legacy fallback
          const legacyAmount = db.engine._getRewardTokens(category, false, status);
          if (legacyAmount > 0) credit(applicant, legacyAmount, 'self');
        }
      }

      // ── 3-level referral chain reward ────────────────────────
      const visited = new Set();
      let current = applicant;
      const levels = [
        { level: 1, key: 'level1' },
        { level: 2, key: 'level2' },
        { level: 3, key: 'level3' },
      ];

      for (const { level, key } of levels) {
        if (!current?.referredBy) break;
        const upline = _users.find((u) => u.id === current.referredBy);
        if (!upline || visited.has(upline.id)) break;
        visited.add(upline.id);

        // Only free_user uplines earn tokens (partners/agents earn commission instead)
        if (upline.role === 'free_user') {
          const tier = newConfig?.referralTiers?.[key];
          if (fires(tier)) {
            credit(upline, amountOf(tier), 'referral', level);
          } else if (!newConfig && level === 1) {
            const legacyAmount = db.engine._getRewardTokens(category, true, status);
            if (legacyAmount > 0) credit(upline, legacyAmount, 'referral', 1);
          }
        }
        current = upline;
      }

      // Backwards compat: explicit referralOwnerId outside the chain
      if (
        caseRecord.referralOwnerId &&
        caseRecord.referralOwnerId !== caseRecord.applicantId &&
        !visited.has(caseRecord.referralOwnerId)
      ) {
        const referrer = _users.find((u) => u.id === caseRecord.referralOwnerId);
        if (referrer && referrer.role === 'free_user') {
          const tier = newConfig?.referralTiers?.level1;
          if (fires(tier)) {
            credit(referrer, amountOf(tier), 'referral', 1);
          } else if (!newConfig) {
            const legacyAmount = db.engine._getRewardTokens(category, true, status);
            if (legacyAmount > 0) credit(referrer, legacyAmount, 'referral', 1);
          }
        }
      }

      return results;
    },

    /**
     * Award a one-time task bonus to a user (e.g., social share, google review, eKYC done).
     * Looks up the task by key in the first active reward config. Idempotent — returns
     * null if the user has already earned this task.
     */
    awardTaskBonus(userId, taskKey, actorUser) {
      const user = _users.find((u) => u.id === userId);
      if (!user) return null;

      const tasks = db.rewardConfigs.getActiveTaskBonuses();
      const task = tasks.find((t) => t.key === taskKey);
      if (!task) return null;

      // Check for duplicate (one-time earn)
      const existing = _tokenTransactions.find(
        (t) => t.userId === userId && t.source === `task:${taskKey}`
      );
      if (existing) return { alreadyEarned: true, taskKey };

      const amount = Number(task.tokens) || 0;
      if (amount <= 0) return null;

      const isVerified = user.kycStatus === 'verified';
      const txnStatus = isVerified ? 'released' : 'pending_kyc';
      if (isVerified) {
        db.users.adjustTokenBalance(userId, amount);
      }

      const txn = db.tokenTransactions.create({
        userId,
        amount,
        type: 'task_bonus',
        source: `task:${taskKey}`,
        status: txnStatus,
      });

      db.engine.notify(
        userId,
        'token',
        isVerified ? `+${amount} task bonus` : `+${amount} task bonus (pending KYC)`,
        `${task.label}: ${amount} tokens ${isVerified ? 'credited' : 'held pending KYC verification'}.`,
        { taskKey, amount, escrowed: !isVerified }
      );

      db.auditLogs.create({
        userId: actorUser?.id || userId,
        userName: actorUser?.name || user.name,
        userRole: actorUser?.role || user.role,
        action: 'task_bonus',
        entityType: 'user',
        entityId: userId,
        description: `Task bonus "${task.label}" — ${amount} tokens`,
      });

      return { amount, task, transaction: txn, escrowed: !isVerified };
    },

    /**
     * Release all pending-KYC rewards for a newly verified user.
     * Walks the user's pending token transactions and flips their status to 'released',
     * adding the amounts to tokenBalance. Also marks their held commissions as 'pending'
     * (or 'paid' if already disbursed).
     */
    releasePendingRewards(userId) {
      const user = _users.find((u) => u.id === userId);
      if (!user) return { released: 0, cases: 0 };

      // 1) Release pending token transactions
      let releasedCount = 0;
      let releasedAmount = 0;
      _tokenTransactions.forEach((t) => {
        if (t.userId === userId && t.status === 'pending_kyc') {
          t.status = 'released';
          t.releasedAt = new Date().toISOString();
          db.users.adjustTokenBalance(userId, t.amount);
          releasedCount += 1;
          releasedAmount += Number(t.amount) || 0;
        }
      });

      // 2) Release pending-KYC commissions on cases where this user is the referral owner
      let caseCount = 0;
      _cases.forEach((c) => {
        if (c.referralOwnerId === userId && c.commissionStatus === 'pending_kyc') {
          c.commissionStatus = c.status === 'Disbursed' ? 'paid' : 'pending';
          c.updatedAt = new Date().toISOString();
          caseCount += 1;
        }
      });

      // Notify + audit
      if (releasedCount > 0 || caseCount > 0) {
        db.engine.notify(
          userId,
          'token',
          'Rewards released',
          `${releasedCount} reward(s) totalling ${releasedAmount} tokens and ${caseCount} commission case(s) have been released after KYC verification.`,
          { releasedAmount, releasedCount, caseCount }
        );
        db.auditLogs.create({
          userId,
          userName: user.name,
          userRole: user.role,
          action: 'release_pending_rewards',
          entityType: 'user',
          entityId: userId,
          description: `Released ${releasedCount} pending transactions (+${releasedAmount} tokens) and ${caseCount} held commissions after KYC verification`,
        });
      }

      return { released: releasedCount, amount: releasedAmount, cases: caseCount };
    },

    /**
     * Award registration bonus tokens up the 3-level referredBy chain when a new user
     * completes registration. Pulls from the most-recently active reward config's
     * registrationBonus block (or falls back to a default).
     */
    awardRegistrationTokens(newUserId, actorUser) {
      const newUser = _users.find((u) => u.id === newUserId);
      if (!newUser || !newUser.referredBy) return [];

      // Use the first active reward config's registrationBonus, or default
      const activeConfigs = db.rewardConfigs.getActive();
      const bonusConfig = activeConfigs[0]?.registrationBonus || {
        level1: 5,
        level2: 3,
        level3: 2,
      };

      const results = [];
      const visited = new Set([newUser.id]);
      let current = newUser;
      const levels = [
        { level: 1, key: 'level1' },
        { level: 2, key: 'level2' },
        { level: 3, key: 'level3' },
      ];

      for (const { level, key } of levels) {
        if (!current?.referredBy) break;
        const upline = _users.find((u) => u.id === current.referredBy);
        if (!upline || visited.has(upline.id)) break;
        visited.add(upline.id);

        if (upline.role === 'free_user') {
          const amount = Number(bonusConfig[key]) || 0;
          if (amount > 0) {
            // KYC escrow: only adjust balance if the upline is verified
            const isVerified = upline.kycStatus === 'verified';
            if (isVerified) {
              db.users.adjustTokenBalance(upline.id, amount);
            }
            db.tokenTransactions.create({
              userId: upline.id,
              amount,
              type: 'bonus',
              source: `Registration bonus — L${level} downline (${newUser.name})`,
              level,
              status: isVerified ? 'released' : 'pending_kyc',
            });
            db.engine.notify(
              upline.id,
              'token',
              isVerified ? `+${amount} registration bonus` : `+${amount} registration bonus (pending KYC)`,
              `${newUser.name} just registered as your L${level} downline. ${amount} tokens ${isVerified ? 'added' : 'held pending KYC verification'}.`,
              { amount, level, escrowed: !isVerified }
            );
            db.auditLogs.create({
              userId: actorUser?.id || 'SYSTEM',
              userName: actorUser?.name || 'System',
              userRole: actorUser?.role || 'system',
              action: 'registration_bonus',
              entityType: 'user',
              entityId: upline.id,
              description: `+${amount} L${level} registration bonus for ${newUser.name}${isVerified ? '' : ' (held pending KYC)'}`,
            });
            results.push({ recipientId: upline.id, amount, level, escrowed: !isVerified });
          }
        }
        current = upline;
      }

      return results;
    },

    /**
     * Track that a partner shared a marketing material — increments share count + audits.
     */
    simulateMaterialShare(materialId, partnerUser) {
      const material = db.marketingMaterials.incrementShareCount(materialId);
      if (!material) return null;
      db.auditLogs.create({
        userId: partnerUser?.id || 'SYSTEM',
        userName: partnerUser?.name || 'System',
        userRole: partnerUser?.role || 'partner',
        action: 'share_material',
        entityType: 'marketing_material',
        entityId: materialId,
        description: `Shared "${material.name}"`,
      });
      return material;
    },

    /**
     * Calculate commission for a case based on the referral owner's role.
     * Uses commissionCalculator.calculateCommissionForCase under the hood.
     * Updates the case's commissionAmount + commissionStatus.
     */
    calculateCommission(caseRecord) {
      if (!caseRecord.referralOwnerId) return { amount: 0 };
      const referrer = _users.find((u) => u.id === caseRecord.referralOwnerId);
      if (!referrer || referrer.role === 'free_user') {
        // Free users get tokens, not commission
        return { amount: 0 };
      }
      const { amount, commission } = calculateCommissionForCase(
        caseRecord,
        _productConfigs,
        referrer
      );

      // KYC escrow: if referrer isn't verified, hold commission as pending_kyc
      let newStatus;
      if (referrer.kycStatus !== 'verified') {
        newStatus = 'pending_kyc';
      } else {
        newStatus = caseRecord.status === 'Disbursed' ? 'paid' : 'pending';
      }

      db.cases.update(caseRecord.id, {
        commissionAmount: amount,
        commissionStatus: newStatus,
      });
      return { amount, commission, recipientId: referrer.id, status: newStatus };
    },

    /**
     * Submit a new case with full effect cascade:
     * - Creates case, awards "submitted" tokens, dispatches notifications, writes audit.
     */
    submitCase(caseData, actorUser) {
      const newCase = db.cases.create({
        ...caseData,
        status: caseData.status || 'Submitted',
      });

      // Notify applicant
      if (newCase.applicantId) {
        db.engine.notify(
          newCase.applicantId,
          'case',
          'Application submitted',
          `Your ${newCase.productName} application (${newCase.id}) has been received and is being processed.`,
          { caseId: newCase.id }
        );
      }

      // Notify referral owner (if any and != applicant)
      if (newCase.referralOwnerId && newCase.referralOwnerId !== newCase.applicantId) {
        db.engine.notify(
          newCase.referralOwnerId,
          'lead',
          'New lead from your referral',
          `${newCase.applicantName} just submitted a ${newCase.productName} application via your referral.`,
          { caseId: newCase.id }
        );
      }

      // Notify admins
      _users
        .filter((u) => u.role === 'admin')
        .forEach((admin) => {
          db.engine.notify(
            admin.id,
            'case',
            'New case submitted',
            `${newCase.applicantName} submitted ${newCase.productName} (${newCase.id})`,
            { caseId: newCase.id }
          );
        });

      // Award "submitted" tokens
      db.engine.awardTokens(newCase, 'submitted');

      // Audit log
      db.auditLogs.create({
        userId: actorUser?.id || newCase.applicantId || 'SYSTEM',
        userName: actorUser?.name || newCase.applicantName || 'System',
        userRole: actorUser?.role || 'system',
        action: 'submit_case',
        entityType: 'case',
        entityId: newCase.id,
        description: `Case submitted for ${newCase.productName}`,
      });

      return deepClone(newCase);
    },

    /**
     * Advance a case status with full cascade:
     * - Update status, calculate commission (if approved/disbursed), award tokens, notify, audit.
     */
    advanceCaseStatus(caseId, newStatus, actorUser) {
      const before = _cases.find((c) => c.id === caseId);
      if (!before) return null;
      const oldStatus = before.status;

      const updated = db.cases.updateStatus(caseId, newStatus);

      // Trigger commission + token cascade for terminal status changes
      if (newStatus === 'Approved' || newStatus === 'Disbursed') {
        db.engine.calculateCommission(updated);
        db.engine.awardTokens(updated, newStatus.toLowerCase());
      }

      // Notify applicant
      if (updated.applicantId) {
        db.engine.notify(
          updated.applicantId,
          'case',
          `Application ${newStatus.toLowerCase()}`,
          `Your ${updated.productName} application (${updated.id}) is now ${newStatus}.`,
          { caseId: updated.id }
        );
      }
      // Notify referral owner
      if (updated.referralOwnerId && updated.referralOwnerId !== updated.applicantId) {
        db.engine.notify(
          updated.referralOwnerId,
          'case',
          `Case status update: ${newStatus}`,
          `${updated.applicantName}'s ${updated.productName} is now ${newStatus}.`,
          { caseId: updated.id }
        );
      }

      // Audit log
      db.auditLogs.create({
        userId: actorUser?.id || 'IMORTGAGE2U',
        userName: actorUser?.name || 'iMortgage2u API',
        userRole: actorUser?.role || 'system',
        action: 'status_update',
        entityType: 'case',
        entityId: caseId,
        description: `Status changed: ${oldStatus} → ${newStatus}`,
      });

      return deepClone({ ...db.cases.getAll().find(c => c.id === caseId), oldStatus });
    },

    /**
     * Simulate iMortgage2u pushing realistic status updates.
     * Picks 2-3 random in-flight cases and advances each via a state machine.
     */
    simulateImortgageSync(actorUser) {
      const inFlightStatuses = ['Submitted', 'In Review', 'Pending Document', 'Pending Bank Decision', 'Approved'];
      const inFlight = _cases.filter((c) => inFlightStatuses.includes(c.status));
      if (inFlight.length === 0) return [];

      // Pick 2-3 random cases to advance
      const count = Math.min(inFlight.length, 2 + Math.floor(Math.random() * 2));
      const shuffled = [...inFlight].sort(() => Math.random() - 0.5);
      const toAdvance = shuffled.slice(0, count);

      // State machine — weighted random next status
      const transitions = {
        'Submitted': [
          { next: 'In Review', weight: 60 },
          { next: 'Pending Document', weight: 25 },
          { next: 'Rejected', weight: 15 },
        ],
        'In Review': [
          { next: 'Pending Bank Decision', weight: 70 },
          { next: 'Pending Document', weight: 20 },
          { next: 'Rejected', weight: 10 },
        ],
        'Pending Document': [
          { next: 'In Review', weight: 80 },
          { next: 'Rejected', weight: 20 },
        ],
        'Pending Bank Decision': [
          { next: 'Approved', weight: 75 },
          { next: 'Rejected', weight: 25 },
        ],
        'Approved': [
          { next: 'Disbursed', weight: 100 },
        ],
      };

      const pickNext = (current) => {
        const opts = transitions[current];
        if (!opts) return null;
        const total = opts.reduce((s, o) => s + o.weight, 0);
        let r = Math.random() * total;
        for (const opt of opts) {
          r -= opt.weight;
          if (r <= 0) return opt.next;
        }
        return opts[opts.length - 1].next;
      };

      const results = [];
      toAdvance.forEach((c) => {
        const next = pickNext(c.status);
        if (!next) return;
        const updated = db.engine.advanceCaseStatus(c.id, next, actorUser);
        results.push({
          caseId: c.id,
          applicantName: c.applicantName,
          productName: c.productName,
          oldStatus: c.status,
          newStatus: next,
        });
      });

      return results;
    },

    /**
     * Redeem tokens for a reward item.
     */
    redeemTokens(userId, item, actorUser) {
      const user = _users.find((u) => u.id === userId);
      if (!user) return { success: false, error: 'User not found' };
      const cost = Number(item.tokens) || 0;
      const currentBalance = Number(user.tokenBalance) || 0;
      if (currentBalance < cost) {
        return { success: false, error: 'Insufficient tokens' };
      }
      // Deduct
      db.users.adjustTokenBalance(userId, -cost);
      // Create redemption record
      const redemption = db.redemptions.create({
        userId,
        itemKey: item.key || item.id,
        itemName: item.name || item.label,
        tokensSpent: cost,
        status: 'completed',
      });
      // Notify
      db.engine.notify(
        userId,
        'redemption',
        'Redemption successful',
        `You redeemed "${item.name || item.label}" for ${cost} tokens.`,
        { redemptionId: redemption.id }
      );
      // Audit
      db.auditLogs.create({
        userId: actorUser?.id || userId,
        userName: actorUser?.name || user.name,
        userRole: actorUser?.role || user.role,
        action: 'redeem_tokens',
        entityType: 'redemption',
        entityId: redemption.id,
        description: `Redeemed "${item.name || item.label}" (-${cost} tokens)`,
      });
      return { success: true, redemption, newBalance: currentBalance - cost };
    },

    /**
     * Generate an invite link with audit + notification.
     */
    createInvite(inviteData, actorUser) {
      const invite = db.invites.create({
        ...inviteData,
        createdBy: actorUser?.id || 'SYSTEM',
      });
      db.auditLogs.create({
        userId: actorUser?.id || 'SYSTEM',
        userName: actorUser?.name || 'System',
        userRole: actorUser?.role || 'admin',
        action: 'create_invite',
        entityType: 'invite',
        entityId: invite.id,
        description: `Created invite link for ${inviteData.role}`,
      });
      return invite;
    },

    /**
     * Approve an invite — creates the user account, sends welcome notification, audits.
     */
    approveInvite(inviteId, actorUser) {
      const result = db.invites.approve(inviteId);
      if (!result) return null;
      // Welcome notification for the new user
      db.engine.notify(
        result.user.id,
        'system',
        'Welcome to iMoney',
        `Your ${result.user.role} account has been activated. Login with your registered email.`,
      );
      // Notify approver
      if (actorUser?.id) {
        db.engine.notify(
          actorUser.id,
          'system',
          'Invite approved',
          `Created account for ${result.user.name}`,
          { userId: result.user.id }
        );
      }
      // Audit
      db.auditLogs.create({
        userId: actorUser?.id || 'SYSTEM',
        userName: actorUser?.name || 'System',
        userRole: actorUser?.role || 'admin',
        action: 'approve_invite',
        entityType: 'invite',
        entityId: inviteId,
        description: `Approved invite — created ${result.user.role} account ${result.user.id}`,
      });
      return result;
    },
  },

  // ─── Utility helpers ───────────────────────────────────────────
  generateId,
  generateReferralCode,
};

export default db;
